import React from "react";
import { Link, useNavigate } from "react-router-dom";
import IOwTableCols from "../../../models/IOwTableCols";
import IPO_Shipment from "../../../models/IPO_Shipment";
import IPurchase_Order from "../../../models/IPurchase_Order";
import ISupplierModel from "../../../models/ISupplierModel";
import IUserUI from "../../../models/IUserUI";
import PurchaseOrderStateEnum from "../../../models/PurchaseOrderStateEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import PurchaseOrderService from "../../../services/PurchaseOrderService";
import UserService from "../../../services/UserService";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwTable from "../../shared/OwTable/OwTable";
import { OwDeleteEditActions } from "../../shared/OwDeleteEditActions/OwDeleteEditActions";
import SimpleConfirmModal from "../../shared/SimpleConfirmModal/SimpleConfirmModal";
import { IUiContextModel } from "@contexts/UiContext";

type Props = {
    purchaseOrder: IPurchase_Order;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    isReadOnly: boolean;
    uiContext: IUiContextModel;
    refreshFn: () => void;
};

const renderShipmentNameFn = (row: IPO_Shipment, index: number) => {
    return (
        <div>
            <Link to={`/purchase_orders/process_shipment/${row.guid}`} className={"pointer"}>
                <OwLabel title={"shipment"} postText={"#:"} className={"me-2 pointer"} />
                {index + 1}
            </Link>
        </div>
    );
};

const renderShipmentStatusFn = (row: IPO_Shipment) => {
    return <div>{row.is_processed ? <OwLabel title={"processed"} /> : <OwLabel title={"open"} />}</div>;
};

const renderCreatedAtFn = (row: IPO_Shipment) => {
    return UserService.parseDate(row.created_at);
};

const renderProcessedByFn = (row: IPO_Shipment) => {
    return row.User?.name;
};

const PurchaseOrderShipmentsDetailView: React.FC<Props> = ({
    purchaseOrder,
    userModel,
    supplierModel,
    isReadOnly,
    uiContext,
    refreshFn,
}) => {
    const navigate = useNavigate();

    const closeModal = () => {
        uiContext.setModalComponent(null);
        uiContext.showModal(false);
    };

    const commitDeleteShipment = async (row: IPO_Shipment) => {
        closeModal();

        try {
            const data = await PurchaseOrderService.deletePOShipment(purchaseOrder.guid, row.guid, userModel, supplierModel);

            if (data.success) {
                refreshFn();
            } else {
                uiContext.notifyFn(null, {
                    message: data.message || "Failed to delete PO shipment",
                    level: NotificationLevelEnum.error,
                    position: NotificationPositionEnum.bc,
                });
            }
        } catch (error: any) {
            console.error("Failed to delete PO shipment:", error);
            uiContext.notifyFn(null, {
                message: error.message || "Failed to delete PO shipment",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            });
        }
    };

    const confirmDeleteShipment = (row: IPO_Shipment) => {
        uiContext.setModalComponent(
            <SimpleConfirmModal
                message={"confirm_delete_shipment"}
                subMessage={"confirm_delete_po_shipment_sub_message"}
                confirmFn={() => commitDeleteShipment(row)}
                cancelFn={closeModal}
            />
        );
        uiContext.showModal(true);
    };

    const renderShipmentActionsFn = (row: IPO_Shipment) => {
        if (row.is_processed || isReadOnly) {
            return null;
        }

        return <OwDeleteEditActions isEditMode={false} hideEdit={true} onTrashClickFn={() => confirmDeleteShipment(row)} />;
    };

    const columns: IOwTableCols[] = [
        {
            isActionsCol: true,
            title: "",
            valueProp: "",
            renderFn: renderShipmentActionsFn,
            renderFnEmitsRow: true,
        },
        {
            title: "shipment",
            valueProp: "",
            renderFn: renderShipmentNameFn,
            renderFnEmitsRow: true,
        },
        {
            title: "status",
            valueProp: "",
            renderFn: renderShipmentStatusFn,
            renderFnEmitsRow: true,
        },
        {
            title: "processed_by",
            valueProp: "",
            renderFn: renderProcessedByFn,
            renderFnEmitsRow: true,
        },
        {
            title: "created_at",
            valueProp: "",
            renderFn: renderCreatedAtFn,
            renderFnEmitsRow: true,
        },
    ];

    const receiveShipment = async () => {
        try {
            const data = await PurchaseOrderService.createPOShipment(
                purchaseOrder.guid,
                purchaseOrder.vendor_source_id,
                userModel,
                supplierModel
            );

            if (data.success && data.poShipment) {
                navigate(`/purchase_orders/process_shipment/${data.poShipment.guid}`);
            } else {
                uiContext.notifyFn(null, {
                    message: data.message || "Failed to create PO shipment",
                    level: NotificationLevelEnum.error,
                    position: NotificationPositionEnum.bc,
                });
            }
        } catch (error: any) {
            console.error("Failed to create PO shipment:", error);
            uiContext.notifyFn(null, {
                message: error.message || "Failed to create PO shipment",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            });
        }
    };

    return (
        <div className="PurchaseOrderShipmentsDetailView">
            <div>
                {purchaseOrder.po_state_id !== PurchaseOrderStateEnum.RECEIVED && !isReadOnly ? (
                    <OwButton title={"receive_shipment"} fn={receiveShipment} className={"btn-warning mb-2"} />
                ) : null}
            </div>
            <div>
                {purchaseOrder.PO_Shipments && purchaseOrder.PO_Shipments.length > 0 ? (
                    <OwTable
                        columns={columns}
                        dataSet={purchaseOrder.PO_Shipments}
                        userModel={userModel}
                        supplierModel={supplierModel}
                        dataFetch={false}
                        initialData={false}
                    />
                ) : (
                    <OwLabel title={"no_shipments"} />
                )}
            </div>
        </div>
    );
};

export default PurchaseOrderShipmentsDetailView;
